import { httpPOST } from './utils';
import { ACTIONS, ICONS } from '../const';
import { selectImageForPage } from '../selectors/content';
import {
    selectPrintJob,
    selectWatermark,
    selectLastJob,
} from '../selectors/print';
import _ from 'underscore';
import R from 'ramda';
import { newNotification } from 'actions/notifications';

const _log = job => (dispatch, getState) => {
    const { content, pageList } = job;
    return httpPOST('/api/v3/print_log/', {
        slug: content.slug,
        first_page: R.head(pageList).pagenum,
        last_page: R.last(pageList).pagenum,
    }).then(() => {
        return selectPrintJob(getState(), job.id);
    });
};

function _resolveImages(job, state) {
    const { content, pageList } = job;
    const getImageForPage = page => {
        return selectImageForPage(state, content, page.pagenum);
    };
    return pageList.map(getImageForPage);
}
const _downloadImages = job => (dispatch, getState) => {
    const urls = _resolveImages(job, getState());
    const total = urls.length;
    dispatch({
        type: ACTIONS.PRINT_PROGRESS,
        id: job.id,
        stage: 'DOWNLOADING',
        percent: 0,
    });

    return Promise.all(
        urls.map(async (url, index) => {
            const res = await fetch(url, { cache: 'reload' });
            const blob = await res.blob();
            return new Promise(resolve => {
                const image = new Image();
                image.onload = () => {
                    dispatch({
                        type: ACTIONS.PRINT_PROGRESS,
                        id: job.id,
                        stage: 'DOWNLOADING',
                        percent: ((index + 1) / total) * 100,
                    });
                    resolve(image);
                };
                image.src = URL.createObjectURL(blob);
            });
        })
    );
};
const _watermark = (job, images) => (dispatch, getState) => {
    const watermarkText = selectWatermark(getState(), job);

    const wrapText = function(context, text, x, y, maxWidth, lineHeight) {
        // Convert the series of words into a set of lines that are
        // all shorter then maxWidth.
        const lines = text.split(' ').reduce(
            (lineList, word) => {
                if (word.trim().length === 0) {
                    return lineList;
                }

                const lastLine = lineList.pop();
                const combinedLine = `${lastLine + word} `;
                const metrics = context.measureText(combinedLine);

                if (metrics.width > maxWidth) {
                    return [...lineList, lastLine, `${word} `];
                }
                return [...lineList, combinedLine];
            },
            ['']
        );

        // Now write all the lines into the canvas
        lines.forEach((line, index) => {
            const lineY = y + lineHeight * index;
            context.fillText(line, x, lineY);
        });
    };

    const scaleOver = function(target, w, h) {
        const newHeight = w / target;
        const newWidth = h * target;
        if (newHeight > h) {
            return [w, newHeight];
        } else if (newWidth > w) {
            return [newWidth, h];
        }
        return [w, h];
    };

    const addWatermark = function(img) {
        const fontSize = 16;
        const lineHeight = fontSize * 1.3;
        const landscape = img.width > img.height;
        const targetRatio = landscape ? 11 / 8.5 : 8.5 / 11;
        const dimentions = scaleOver(targetRatio, img.width, img.height);
        const canvas = document.createElement('canvas');

        canvas.width = _.min(dimentions);
        canvas.height = _.max(dimentions);
        let xMargin = (canvas.width - img.width) / 2;
        let yMargin = (canvas.height - img.height) / 2;

        const ctx = canvas.getContext('2d');
        if (landscape) {
            ctx.translate(canvas.width, 0);
            ctx.rotate(Math.PI / 2);
            yMargin = (canvas.width - img.height) / 2;
            xMargin = (canvas.height - img.width) / 2;
        }
        ctx.drawImage(img, xMargin, yMargin);
        ctx.font = `${fontSize}px sans-serif`;
        ctx.textBaseline = 'top';
        wrapText(
            ctx,
            watermarkText,
            xMargin,
            yMargin,
            canvas.width - xMargin * 2,
            lineHeight
        );
        return canvas.toDataURL('image/png');
    };

    return images.map(addWatermark);
};

export const newPrintJob = (content, pageList, onClose) => (
    dispatch,
    getState
) => {
    dispatch({
        type: ACTIONS.NEW_PRINT_JOB,
        content,
        pageList,
    });
    const job = selectLastJob(getState());

    dispatch(_log(job))
        .then(() => dispatch(_downloadImages(job)))
        .then(images => dispatch(_watermark(job, images)))
        .then(urls => {
            dispatch({
                type: ACTIONS.PRINT_DONE,
                id: job.id,
                urls,
            });
        })
        .catch(() =>
            dispatch(
                newNotification(
                    'Error printing',
                    `Could not print "${content.title}"`,
                    { icon: ICONS.FAIL, dismissable: true }
                )
            )
        )
        .finally(onClose);

    return job.id;
};



// WEBPACK FOOTER //
// ./src/actions/print.js